/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CardExpense } from '../models/card-expense';
import { CardMonthlyBalance } from '../models/card-monthly-balance';
import { CardResponse } from '../models/card-response';
import { EditPinRequest } from '../models/edit-pin-request';
import { EphemeralKeyRequest } from '../models/ephemeral-key-request';
import { EphemeralKeyResponse } from '../models/ephemeral-key-response';
import { PaginatedCardExpenseResult } from '../models/paginated-card-expense-result';

/**
 * Cards
 */
@Injectable({
  providedIn: 'root',
})
export class CardService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getCardStatements
   */
  static readonly GetCardStatementsPath =
    '/api/mobile/card/byproject/{projectId}/statement';

  /**
   * Get monthly card expenses.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCardStatements()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCardStatements$Response(params: {
    projectId: number;
    context?: HttpContext;
  }): Observable<StrictHttpResponse<Array<CardMonthlyBalance>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CardService.GetCardStatementsPath,
      'get'
    );
    if (params) {
      rb.path('projectId', params.projectId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<CardMonthlyBalance>>;
        })
      );
  }

  /**
   * Get monthly card expenses.
   *
   *
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCardStatements$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCardStatements(params: {
    projectId: number;
    context?: HttpContext;
  }): Observable<Array<CardMonthlyBalance>> {
    return this.getCardStatements$Response(params).pipe(
      map(
        (r: StrictHttpResponse<Array<CardMonthlyBalance>>) =>
          r.body as Array<CardMonthlyBalance>
      )
    );
  }

  /**
   * Path part for operation getCardTransactionsPaged
   */
  static readonly GetCardTransactionsPagedPath =
    '/api/mobile/card/byproject/{projectId}/expenses/paged';

  /**
   * Get paginated card expenses for a project.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCardTransactionsPaged()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCardTransactionsPaged$Response(params: {
    /**
     * ID of the project.
     */
    projectId: number;

    /**
     * Number of expenses to return in this page.
     */
    limit?: number;

    /**
     * Cursor for pagination. If provided, returns expenses after the given authorization id.
     */
    startingAfter?: string;
    context?: HttpContext;
  }): Observable<StrictHttpResponse<PaginatedCardExpenseResult>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CardService.GetCardTransactionsPagedPath,
      'get'
    );
    if (params) {
      rb.path('projectId', params.projectId, {});
      rb.query('limit', params.limit, {});
      rb.query('startingAfter', params.startingAfter, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PaginatedCardExpenseResult>;
        })
      );
  }

  /**
   * Get paginated card expenses for a project.
   *
   *
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCardTransactionsPaged$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCardTransactionsPaged(params: {
    /**
     * ID of the project.
     */
    projectId: number;

    /**
     * Number of expenses to return in this page.
     */
    limit?: number;

    /**
     * Cursor for pagination. If provided, returns expenses after the given authorization id.
     */
    startingAfter?: string;
    context?: HttpContext;
  }): Observable<PaginatedCardExpenseResult> {
    return this.getCardTransactionsPaged$Response(params).pipe(
      map(
        (r: StrictHttpResponse<PaginatedCardExpenseResult>) =>
          r.body as PaginatedCardExpenseResult
      )
    );
  }

  /**
   * Path part for operation getCardTransactionsByYearAndMonthPaged
   */
  static readonly GetCardTransactionsByYearAndMonthPagedPath =
    '/api/mobile/card/byproject/{projectId}/transactions/paged';

  /**
   * Get paginated card transactions for a project by year and month.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCardTransactionsByYearAndMonthPaged()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCardTransactionsByYearAndMonthPaged$Response(params: {
    /**
     * ID of the project.
     */
    projectId: number;

    /**
     * Number of expenses to return in this page.
     */
    limit: number;

    /**
     * number of the month in which the transactions occurred, from 1 to 12.
     */
    month: number;

    /**
     * year in which the transactions occurred.
     */
    year: number;

    /**
     * Cursor for pagination. If provided, returns transactions after the given transaction id.
     */
    startingAfter?: string;
    context?: HttpContext;
  }): Observable<StrictHttpResponse<PaginatedCardExpenseResult>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CardService.GetCardTransactionsByYearAndMonthPagedPath,
      'get'
    );
    if (params) {
      rb.path('projectId', params.projectId, {});
      rb.query('limit', params.limit, {});
      rb.query('month', params.month, {});
      rb.query('year', params.year, {});
      rb.query('startingAfter', params.startingAfter, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PaginatedCardExpenseResult>;
        })
      );
  }

  /**
   * Get paginated card transactions for a project by year and month.
   *
   *
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCardTransactionsByYearAndMonthPaged$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCardTransactionsByYearAndMonthPaged(params: {
    /**
     * ID of the project.
     */
    projectId: number;

    /**
     * Number of expenses to return in this page.
     */
    limit: number;

    /**
     * number of the month in which the transactions occurred, from 1 to 12.
     */
    month: number;

    /**
     * year in which the transactions occurred.
     */
    year: number;

    /**
     * Cursor for pagination. If provided, returns transactions after the given transaction id.
     */
    startingAfter?: string;
    context?: HttpContext;
  }): Observable<PaginatedCardExpenseResult> {
    return this.getCardTransactionsByYearAndMonthPaged$Response(params).pipe(
      map(
        (r: StrictHttpResponse<PaginatedCardExpenseResult>) =>
          r.body as PaginatedCardExpenseResult
      )
    );
  }

  /**
   * Path part for operation getCardStatementDetailPdf
   */
  static readonly GetCardStatementDetailPdfPath =
    '/api/mobile/card/byproject/{projectId}/statement/{year}/{month}/pdf';

  /**
   * Get card statement PDF.
   *
   * Generates and returns the PDF card statement for the specified project, year and month for the currently authenticated user.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCardStatementDetailPdf()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCardStatementDetailPdf$Response(params: {
    /**
     * Project identifier
     */
    projectId: number;

    /**
     * Statement year
     */
    year: number;

    /**
     * Statement month (1–12)
     */
    month: number;

    /**
     * Time zone identifier (e.g., &quot;Europe/Rome&quot;), used to format the time correctly in the PDF.
     */
    timezone: string;
    context?: HttpContext;
  }): Observable<StrictHttpResponse<Blob>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CardService.GetCardStatementDetailPdfPath,
      'get'
    );
    if (params) {
      rb.path('projectId', params.projectId, {});
      rb.path('year', params.year, {});
      rb.path('month', params.month, {});
      rb.query('timezone', params.timezone, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'blob',
          accept: 'application/pdf',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Blob>;
        })
      );
  }

  /**
   * Get card statement PDF.
   *
   * Generates and returns the PDF card statement for the specified project, year and month for the currently authenticated user.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCardStatementDetailPdf$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCardStatementDetailPdf(params: {
    /**
     * Project identifier
     */
    projectId: number;

    /**
     * Statement year
     */
    year: number;

    /**
     * Statement month (1–12)
     */
    month: number;

    /**
     * Time zone identifier (e.g., &quot;Europe/Rome&quot;), used to format the time correctly in the PDF.
     */
    timezone: string;
    context?: HttpContext;
  }): Observable<Blob> {
    return this.getCardStatementDetailPdf$Response(params).pipe(
      map((r: StrictHttpResponse<Blob>) => r.body as Blob)
    );
  }

  /**
   * Path part for operation getCardStatementDetailByYearAndMonth
   */
  static readonly GetCardStatementDetailByYearAndMonthPath =
    '/api/mobile/card/byproject/{projectId}/statement/{year}/{month}';

  /**
   * Get the detail of the card statement for a project by year and month.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCardStatementDetailByYearAndMonth()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCardStatementDetailByYearAndMonth$Response(params: {
    /**
     * ID of the project.
     */
    projectId: number;

    /**
     * number of the month in which the transactions occurred, from 1 to 12.
     */
    month: number;

    /**
     * year in which the transactions occurred.
     */
    year: number;
    context?: HttpContext;
  }): Observable<StrictHttpResponse<CardMonthlyBalance>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CardService.GetCardStatementDetailByYearAndMonthPath,
      'get'
    );
    if (params) {
      rb.path('projectId', params.projectId, {});
      rb.path('month', params.month, {});
      rb.path('year', params.year, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CardMonthlyBalance>;
        })
      );
  }

  /**
   * Get the detail of the card statement for a project by year and month.
   *
   *
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCardStatementDetailByYearAndMonth$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCardStatementDetailByYearAndMonth(params: {
    /**
     * ID of the project.
     */
    projectId: number;

    /**
     * number of the month in which the transactions occurred, from 1 to 12.
     */
    month: number;

    /**
     * year in which the transactions occurred.
     */
    year: number;
    context?: HttpContext;
  }): Observable<CardMonthlyBalance> {
    return this.getCardStatementDetailByYearAndMonth$Response(params).pipe(
      map(
        (r: StrictHttpResponse<CardMonthlyBalance>) =>
          r.body as CardMonthlyBalance
      )
    );
  }

  /**
   * Path part for operation getCardExpensesByProjectId
   */
  static readonly GetCardExpensesByProjectIdPath =
    '/api/mobile/card/byproject/{projectId}/expenses';

  /**
   * Get card transactions for a project.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCardExpensesByProjectId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCardExpensesByProjectId$Response(params: {
    /**
     * ID of the project
     */
    projectId: number;
    context?: HttpContext;
  }): Observable<StrictHttpResponse<Array<CardExpense>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CardService.GetCardExpensesByProjectIdPath,
      'get'
    );
    if (params) {
      rb.path('projectId', params.projectId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<CardExpense>>;
        })
      );
  }

  /**
   * Get card transactions for a project.
   *
   *
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCardExpensesByProjectId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCardExpensesByProjectId(params: {
    /**
     * ID of the project
     */
    projectId: number;
    context?: HttpContext;
  }): Observable<Array<CardExpense>> {
    return this.getCardExpensesByProjectId$Response(params).pipe(
      map(
        (r: StrictHttpResponse<Array<CardExpense>>) =>
          r.body as Array<CardExpense>
      )
    );
  }

  /**
   * Path part for operation getCardByProjectId
   */
  static readonly GetCardByProjectIdPath =
    '/api/mobile/card/byproject/{projectId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCardByProjectId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCardByProjectId$Response(params: {
    projectId: number;
    context?: HttpContext;
  }): Observable<StrictHttpResponse<CardResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CardService.GetCardByProjectIdPath,
      'get'
    );
    if (params) {
      rb.path('projectId', params.projectId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CardResponse>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCardByProjectId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCardByProjectId(params: {
    projectId: number;
    context?: HttpContext;
  }): Observable<CardResponse> {
    return this.getCardByProjectId$Response(params).pipe(
      map((r: StrictHttpResponse<CardResponse>) => r.body as CardResponse)
    );
  }

  /**
   * Path part for operation generateEphemeralKey
   */
  static readonly GenerateEphemeralKeyPath = '/api/mobile/card/key';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `generateEphemeralKey()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  generateEphemeralKey$Response(params: {
    context?: HttpContext;
    body: EphemeralKeyRequest;
  }): Observable<StrictHttpResponse<EphemeralKeyResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CardService.GenerateEphemeralKeyPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<EphemeralKeyResponse>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `generateEphemeralKey$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  generateEphemeralKey(params: {
    context?: HttpContext;
    body: EphemeralKeyRequest;
  }): Observable<EphemeralKeyResponse> {
    return this.generateEphemeralKey$Response(params).pipe(
      map(
        (r: StrictHttpResponse<EphemeralKeyResponse>) =>
          r.body as EphemeralKeyResponse
      )
    );
  }

  /**
   * Path part for operation updatePin
   */
  static readonly UpdatePinPath = '/api/mobile/card/pin';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updatePin()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePin$Response(params: {
    context?: HttpContext;
    body: EditPinRequest;
  }): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CardService.UpdatePinPath,
      'put'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updatePin$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePin(params: {
    context?: HttpContext;
    body: EditPinRequest;
  }): Observable<void> {
    return this.updatePin$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }
}
